import { useId, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { Chevron } from './Chevron'
import { Icon } from './Icon'

export function Switch({
  checked,
  onChange,
  label,
  tint,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  tint?: string
}) {
  return (
    <button
      type="button"
      className="switch-row"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
    >
      <span>{label}</span>
      <span
        className="switch"
        data-on={checked}
        style={tint ? ({ '--sw-tint': tint } as CSSProperties) : undefined}
      />
    </button>
  )
}

export function Meter({ value, tint }: { value: number; tint?: string }) {
  return (
    <div className="meter" style={tint ? ({ '--meter-tint': tint } as CSSProperties) : undefined}>
      <i style={{ width: `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%` }} />
    </div>
  )
}

export function Dial({ label, value, tint }: { label: string; value: number; tint?: string }) {
  return (
    <div className="dial">
      <div className="dial-top">
        <span>{label}</span>
        <b className="num">{Math.round(value * 100)}</b>
      </div>
      <Meter value={value} tint={tint} />
    </div>
  )
}

export function Chip({
  children,
  tint,
  dot = true,
}: {
  children: ReactNode
  tint?: string
  dot?: boolean
}) {
  return (
    <span className="chip" style={tint ? ({ '--chip-tint': tint } as CSSProperties) : undefined}>
      {dot && <i />}
      {children}
    </span>
  )
}

export interface FieldNote {
  id: string
  name: string
  text: string
}

/**
 * A labelled control, optionally with what its options actually mean.
 *
 * The notes are not written for the panel — they are the exact briefs the model
 * is given for each option (`LENS_BRIEF`, `TEMPER_BRIEF`). Paraphrasing them
 * here would create two descriptions of one thing that drift apart, and the
 * more useful answer to "what does Reaching do?" is the instruction itself.
 *
 * Closed by default: it is reference, not chrome.
 */
export function Field({
  label,
  children,
  notes,
  active,
}: {
  label: string
  children: ReactNode
  notes?: FieldNote[]
  active?: string
}) {
  const [open, setOpen] = useState(false)
  const id = useId()
  return (
    <div className="field">
      <div className="field-head">
        <span className="eyebrow">{label}</span>
        {notes && notes.length > 0 && (
          <button
            type="button"
            className="infobtn"
            aria-expanded={open}
            aria-controls={id}
            aria-label={`What the ${label.toLowerCase()} options mean`}
            title={`What the ${label.toLowerCase()} options mean`}
            onClick={() => setOpen((v) => !v)}
          >
            <Icon name="info" size={13} />
          </button>
        )}
      </div>
      {children}
      {/* Rendered always and hidden with the attribute, so `aria-controls`
          never points at an id that is not in the document. */}
      {notes && notes.length > 0 && (
        <dl className="fieldnote" id={id} hidden={!open}>
          {notes.map((n) => (
            <div key={n.id} data-active={n.id === active || undefined}>
              <dt>{n.name}</dt>
              <dd>{n.text}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}

/**
 * A collapsed annotation: the same chrome as `Field`, but the payload is prose
 * and there is no control between the caption and the note.
 *
 * The whole caption line is the press (D335), with the card fold's chevron —
 * right while closed, down while open. It used to open from a 16px ⓘ beside a
 * caption that did not look like a control, which is the card header the study
 * found first (D157), on a target under every touch guideline; and an info icon
 * says "explain this", not "there is more below". `Field` keeps its ⓘ, because
 * there the icon does gloss a control. The hint rides the tooltip; the caption's
 * own words name the button.
 *
 * Closed by default — it is reference, not chrome.
 */
export function Disclosure({
  label,
  meta,
  hint,
  children,
}: {
  label: string
  meta?: string
  hint: string
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const id = useId()
  return (
    <div className="field">
      <button
        type="button"
        className="field-head field-head--press"
        aria-expanded={open}
        aria-controls={id}
        title={hint}
        onClick={() => setOpen((v) => !v)}
      >
        <Chevron open={open} />
        <span className="eyebrow">{label}</span>
        {meta && <span className="field-meta">{meta}</span>}
      </button>
      <div className="fieldnote" id={id} hidden={!open}>
        {children}
      </div>
    </div>
  )
}

export function Range({
  value,
  min = 0,
  max = 1,
  step = 0.01,
  onChange,
  'aria-label': ariaLabel,
}: {
  value: number
  min?: number
  max?: number
  step?: number
  onChange: (v: number) => void
  'aria-label': string
}) {
  const pct = ((value - min) / (max - min)) * 100
  return (
    <input
      type="range"
      className="range"
      aria-label={ariaLabel}
      min={min}
      max={max}
      step={step}
      value={value}
      style={{ '--pct': `${pct}%` } as CSSProperties}
      onChange={(e) => onChange(Number(e.target.value))}
    />
  )
}
